/* eslint-disable react/no-unused-prop-types */
import React, { useRef, useEffect } from 'react';
import PersonalizedWidgetRenderer from '@nykaa/deals-personalization';
import { asyncImpressionsHandler } from 'shared/helpers/impressions/asyncImpressionsHandler';
import { syncImpressionsHandler } from 'shared/helpers/impressions/syncImpressionsHandler';
import { ApiHelper } from '../../api';
import { getDealsEndpoint } from '../../api/urls';
import { useUserData } from 'shared/hooks/auth';
import { getVariantsForPageType } from 'helpers/experimentV2';
import { State } from 'shared/store/types';
import useCartOperations from 'components/CartOperations';
import { connect, useSelector } from 'react-redux';
import { IMPRESSION_TYPE } from '@nykaa/utils/retina/constants/index';
import {
  getApiParams,
  isIndianRegion,
  getDnPageTypeFromWidgetData,
} from 'shared/helpers';

interface PaginationParams {
  isPaginationEnabled: boolean;
  bufferSize: number;
  pageLimit: number;
  remainingInventoryIds: string[];
  initialInventoryCount: number;
  noImpressionImmediatePaginationCall?: boolean;
}
interface ApiParams {
  params: object;
  paginationParams: PaginationParams;
}

export interface CustomWidgetRendererProps {
  widgetList: any[];
  isServer: boolean;
  impressionEvent?: string;
  eagerLoadImages?: number;
  eagerLoadInventories?: number;
  theme?: any;
  personalizationEnabled?: boolean;
  variants?: any;
  pageName?: string;
  isPaginationEnabled?: any;
  apiParams?: ApiParams;
  remainingInventoryIds?: string[];
  noImpressionImmediatePaginationCall?: boolean;
  allWidgetsLength?: number;
  queryParamsObject?: object;
  currentBatchLoadingCb?: Function;
  pageData?: string;
  reduxState?: State;
  userSegment?: Record<string, any>;
}

const mapStateToProps = (
  state: State,
  ownProps: CustomWidgetRendererProps
) => ({
  personalizationEnabled: !!state.remoteConfig.switches.personalizationEnabled,
  isPaginationEnabled: !!state.remoteConfig.switches.isPaginationEnabled,
  apiParams: getApiParams(state, ownProps) || {},
  reduxState: state,
});

const CustomWidgetRenderer = ({
  widgetList,
  isServer,
  impressionEvent,
  eagerLoadImages,
  eagerLoadInventories,
  theme,
  personalizationEnabled,
  pageName = '',
  pageData = 'default',
  isPaginationEnabled = false,
  apiParams,
  currentBatchLoadingCb = () => {},
  reduxState,
  userSegment = {},
}: CustomWidgetRendererProps) => {
  // Get DN-Page-Type from widget response, and pass to getVariantsForPageType
  // so that variants of that pageType will be fetched
  const dnPageType = getDnPageTypeFromWidgetData({ widgets: widgetList });
  const variants = getVariantsForPageType(reduxState, dnPageType);
  const regionCountry = useSelector(
    (state: State) => state.appReducer?.region?.regionHeaderCountry
  );
  const { rootMargin, dnConfigs } = useSelector(
    (state: State) => state.remoteConfig?.configs
  );
  const { dnSwitches } = useSelector(
    (state: State) => state.remoteConfig?.switches
  );
  const dnRemoteConfigs = {
    configs: dnConfigs,
    switches: dnSwitches,
  };
  const { defaultEagerLoadInventories } = dnRemoteConfigs?.configs || {};
  const { id } = useUserData();
  const userIdRef = useRef('');
  const setUserId = (newUserId: string = '') => {
    userIdRef.current = newUserId;
  };
  useEffect(() => {
    setUserId(id);
  }, [id]);

  const asyncImpressionCallback = async (impressions: any = []) =>
    asyncImpressionsHandler(
      impressions,
      userIdRef?.current,
      isIndianRegion(regionCountry)
    );

  const syncImpressionCallback = (
    impressions: any = [],
    impressionType: string = IMPRESSION_TYPE.BANNER
  ) =>
    syncImpressionsHandler(
      impressions,
      impressionType,
      isIndianRegion(regionCountry)
    );
  const finalEagerLoadInventories =
    eagerLoadInventories || defaultEagerLoadInventories;
  const { ...cta } = useCartOperations(pageName);

  const { CartOperations, ctaCallbacks } = cta || {};
  if (personalizationEnabled) {
    return (
      <>
        <PersonalizedWidgetRenderer
          widgetList={widgetList}
          isServer={isServer}
          isDesktop={false}
          isPersonalizationSupported={personalizationEnabled}
          impressionEvent={impressionEvent}
          asyncImpressionCallback={asyncImpressionCallback}
          syncImpressionCallback={syncImpressionCallback}
          variants={variants}
          ApiHelper={ApiHelper}
          pageData={pageData}
          dealsEndpoint={getDealsEndpoint() || ''}
          store={__PLATFORM__ || 'nykaa'}
          eagerLoadImages={eagerLoadImages}
          eagerLoadInventories={finalEagerLoadInventories}
          theme={theme}
          cta={ctaCallbacks}
          isPaginationEnabled={isPaginationEnabled}
          apiParams={apiParams as ApiParams}
          currentBatchLoadingCb={currentBatchLoadingCb}
          rootMargin={rootMargin}
          remoteConfigs={dnRemoteConfigs}
          contextPayload={userSegment}
        />
        {CartOperations && <CartOperations />}
      </>
    );
  }

  return (
    <>
      <PersonalizedWidgetRenderer
        widgetList={widgetList}
        isServer={isServer}
        isDesktop={false}
        isPersonalizationSupported={personalizationEnabled}
        impressionEvent={impressionEvent}
        asyncImpressionCallback={asyncImpressionCallback}
        syncImpressionCallback={syncImpressionCallback}
        variants={variants}
        ApiHelper={ApiHelper}
        pageData={pageData}
        dealsEndpoint={getDealsEndpoint() || ''}
        store={__PLATFORM__ || 'nykaa'}
        eagerLoadImages={eagerLoadImages}
        eagerLoadInventories={finalEagerLoadInventories}
        theme={theme}
        cta={ctaCallbacks}
        isPaginationEnabled={isPaginationEnabled}
        apiParams={apiParams as ApiParams}
        currentBatchLoadingCb={currentBatchLoadingCb}
        rootMargin={rootMargin}
        remoteConfigs={dnRemoteConfigs}
        contextPayload={userSegment}
      />
      {CartOperations && <CartOperations />}
    </>
  );
};

export default connect(mapStateToProps, null)(CustomWidgetRenderer);
